/**
 * The merged document's own redaction-data asset (VE-30104).
 *
 * `project-redaction-data` on the PROJECT TDO: the project-level analogue of
 * the per-document `document-redaction-data` asset. An asset rather than a
 * `details` key because it stores the same watermark array the per-document
 * asset does, through the same sanitizer — in `details` that would be one
 * data shape with two homes and two readers.
 *
 * APPEND-ONLY: `createAsset` adds, nothing updates in place, so the newest by
 * `createdDateTime` wins (the `pickNewestBy` rule used throughout this repo).
 *
 * IDENTITY IS A MODIFIED STAMP PER CONCERN, compared only for EQUALITY.
 *
 * Not a version counter: a counter is minted from whatever this session last
 * read, so two reviewers on v3 each write a different v4 and an output under
 * one compares equal to the other, and a failed parse restarts it at a value
 * that collides with everything before. A stamp is derived from nothing, so
 * neither happens — the second writer and the post-failure writer both stamp
 * a different instant.
 *
 * Not the asset id either, though that fixes the same two things. An id
 * cannot be written by the body that is about to become that asset, so it
 * needs an omit-this-concern-and-fill-it-in-on-read rule; a stamp is just
 * written. Both verdicts test equality and never order, so whose clock minted
 * it does not matter — see `watermarkStalenessVerdict` and
 * `batesNumberingVerdict`. The residual risk is two writes inside one
 * millisecond, which is strictly smaller than the collision it replaces.
 *
 * PER CONCERN, not per body — `watermarksModifiedDateTime` and
 * `batesModifiedDateTime` — so a Bates-only edit cannot stale every member's
 * watermarks. A write stamps only the concern it touched and carries the
 * other's stamp forward unchanged. Outputs record the stamp of the concern
 * they were burned under.
 *
 * PROJECTED INTO THE ASSET'S `details` as well — see
 * `buildProjectRedactionAssetDetails` for what and why. Note that every
 * caller of the staleness verdicts today is in the EDITOR, which holds the
 * body already: `collectBatesBehindTdoIds` runs over the Documents panel's
 * rows, not the folder view's, and the folder view's download gate does not
 * check project staleness at all. The projection is there for that gate when
 * it does.
 *
 * NOTHING IS WRITTEN TO MEMBER TDOs. Members resolve the set's marks on every
 * render from the project plus their own membership — which is why the marks
 * follow the reviewer to the next document untouched, and why a member added
 * later inherits them for free.
 */
import {
  watermarksForStorage,
  watermarksFromAssetBody,
  watermarksOffFromAssetBody,
} from '@redact-modules/documents/helpers';
import type {
  AppliedWatermark,
  BatesStampSettings,
} from '@redact-modules/documents/models';
import { parseBatesSettings } from './batesStamp';
import type { TDOId } from '@common-modules/universal/models/Brands';

/** assetType the merged document's settings are stored under. */
export const PROJECT_REDACTION_ASSET_TYPE = 'project-redaction-data';

/**
 * The format version written into new bodies. 1.0.0 carries `watermarks` and
 * `watermarksOff`. Adding a field is additive and does not bump; changing a
 * key's shape, or letting a body say the same thing two ways, does.
 */
export const PROJECT_REDACTION_ASSET_FORMAT_VERSION = '1.0.0' as const;

/**
 * The oldest body this build will read — a floor with no ceiling, matching the
 * document asset: an older build still loads a newer body and ignores keys it
 * does not know, so a bump never strands a reviewer.
 */
const MIN_SUPPORTED_PROJECT_REDACTION_FORMAT = '1.0.0' as const;

/** What a `project-redaction-data` body holds. */
export interface ProjectRedactionData {
  /**
   * The asset this record was read from, or written as — server-minted, and
   * the identity of the body as a whole. See the header.
   *
   * Null only between an optimistic write and its confirmation, when no asset
   * exists yet. A burn in that window records nothing and reads `behind`,
   * which is the safe direction and resolves on the next load.
   */
  readonly assetId: string | null;
  /**
   * Which project this body says it belongs to — self-identification, so a
   * record copied or restored onto the wrong project is caught by a reader
   * rather than silently marking the wrong release. The per-document body
   * carries the same cross-check, with a comment noting corruption seen in
   * production. Null on bodies written before the field existed.
   */
  readonly projectTdoId: string | null;
  /**
   * When the WATERMARKS last changed. The identity an output records; see
   * the header. Null when they have never been set.
   */
  readonly watermarksModifiedDateTime: string | null;
  /** The set's watermarks, applied to every member. */
  readonly watermarks: readonly AppliedWatermark[];
  /** The set's own off switch, distinct from any member's. */
  readonly watermarksOff: boolean;
  /**
   * When the BATES SETTINGS last changed, on the same terms. Null when the
   * set carries no stamping — which is a real answer, distinct from the
   * record not having been read, and the reason `ProjectRedactionRecord`
   * is a four-state union.
   */
  readonly batesModifiedDateTime: string | null;
  /** The set's Bates settings, or null when it stamps nothing. */
  readonly bates: BatesStampSettings | null;
}

/**
 * What this session knows about a project's record.
 *
 * FOUR STATES AS A UNION rather than `record | null | undefined`: that triple
 * let five separate Bates readers collapse "not looked up" into "carries
 * none" and answer confidently while a lookup was outstanding. A caller here
 * must name the state it is handling.
 */
export type ProjectRedactionRecord =
  | { readonly status: 'unread' }
  | { readonly status: 'failed' }
  | { readonly status: 'none' }
  | {
      readonly status: 'loaded';
      readonly data: ProjectRedactionData;
      /**
       * Set-wide image marks whose artwork this read could NOT restore.
       *
       * On the wrapper, not in `data`: that is the body and must round-trip
       * to storage unchanged. Reported rather than inferred, because a failed
       * hydration leaves the mark's pointer in place — so without this the
       * row reads as healthy while the page draws nothing and the burn drops
       * it.
       */
      readonly artworkFailures?: readonly string[];
    };

export const UNREAD: ProjectRedactionRecord = { status: 'unread' };

/**
 * A record that carries nothing — what a `none` set is, written out.
 *
 * SO THE FIRST WRITE HAS SOMETHING TO COMPOSE ONTO. `none` is a read answer:
 * the set has been looked up and has no asset yet, which is where every
 * project starts. A writer that demanded `loaded` could only ever add a
 * second concern to a body some other concern had already created — which is
 * exactly how numbering came to be unsaveable on any set nobody had
 * watermarked first.
 *
 * Not for `unread` or `failed`. Those say the body may hold anything, and
 * composing onto this would write a record that silently drops it.
 */
export const EMPTY_PROJECT_REDACTION_DATA: ProjectRedactionData = {
  assetId: null,
  projectTdoId: null,
  watermarksModifiedDateTime: null,
  watermarks: [],
  watermarksOff: false,
  batesModifiedDateTime: null,
  bates: null,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Is a dotted version string at or above the floor?
 *
 * Compared PART BY PART as numbers. String comparison reads '1.10.0' as older
 * than '1.9.0', which is the kind of bug that only appears on the tenth
 * revision and then refuses every body in the field.
 */
export const isProjectRedactionFormatSupported = (
  version: unknown
): boolean => {
  if (typeof version !== 'string') {
    // A body with no version predates nothing — this is the first format — so
    // it was not written by any build of this app.
    return false;
  }
  const parts = version.split('.').map((p) => Number.parseInt(p, 10));
  const floor = MIN_SUPPORTED_PROJECT_REDACTION_FORMAT.split('.').map((p) =>
    Number.parseInt(p, 10)
  );
  if (parts.length !== 3 || parts.some((p) => !Number.isInteger(p))) {
    return false;
  }
  for (let i = 0; i < 3; i += 1) {
    const got = parts[i] ?? 0;
    const want = floor[i] ?? 0;
    if (got !== want) {
      return got > want;
    }
  }
  return true;
};

/**
 * Read a `project-redaction-data` body, or null if it carries nothing usable.
 *
 * A TRUST BOUNDARY: this body is object storage any org member can write
 * through the public API, it is read on every open, and what comes out of it
 * is drawn onto a released record.
 *
 * The watermark array goes through `watermarksFromAssetBody` — the SAME
 * sanitizer the per-document asset uses, which already clamps opacity, text,
 * rotation, geometry and anchors and refuses a remote or non-PNG raster. A
 * parallel sanitizer here would be two implementations of one rule, free to
 * drift apart.
 */
export const parseProjectRedactionData = (
  body: unknown,
  /** The asset the body came from — identity, see `assetId`. */
  assetId: string | null = null
): ProjectRedactionData | null => {
  if (!isRecord(body)) {
    return null;
  }
  if (!isProjectRedactionFormatSupported(body.assetFormatVersion)) {
    return null;
  }
  const stamp = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() ? value : null;
  return {
    assetId,
    projectTdoId:
      typeof body.projectTdoId === 'string' && body.projectTdoId.trim()
        ? body.projectTdoId
        : null,
    watermarksModifiedDateTime: stamp(body.watermarksModifiedDateTime),
    watermarks: watermarksFromAssetBody(body),
    watermarksOff: watermarksOffFromAssetBody(body),
    batesModifiedDateTime: stamp(body.batesModifiedDateTime),
    // CLAMPED, like every other Bates reader. This body is hand-editable
    // server JSON exactly as the TDO details it replaced were, so it is a
    // trust boundary: a slightly-off stamp an operator can see and correct
    // beats silently losing the sequence on a document that had one.
    bates: parseBatesSettings(body.bates),
  };
};

/**
 * The body to write for a new version of the record.
 *
 * Through `watermarksForStorage`, the SAME stripper the per-document body
 * uses. It drops the derived PNG always and the base raster once an
 * `imageAssetId` points at the asset that replaces it — without it this body
 * would carry megabytes of base64 for every member of the set to download,
 * which is the readability problem asset format 1.3.0 existed to fix. Its own
 * header records that the one path which skipped it stored a 2 MB logo twice
 * per document; this would have been the second such path.
 */
export const buildProjectRedactionBody = (
  data: ProjectRedactionData
): Record<string, unknown> => ({
  assetFormatVersion: PROJECT_REDACTION_ASSET_FORMAT_VERSION,
  ...(data.projectTdoId ? { projectTdoId: data.projectTdoId } : {}),
  ...(data.watermarksModifiedDateTime
    ? { watermarksModifiedDateTime: data.watermarksModifiedDateTime }
    : {}),
  watermarks: watermarksForStorage(data.watermarks),
  watermarksOff: data.watermarksOff,
  ...(data.batesModifiedDateTime
    ? { batesModifiedDateTime: data.batesModifiedDateTime }
    : {}),
  ...(data.bates ? { bates: data.bates } : {}),
});

/**
 * What a reader should compare a watermark stamp against: the stamp, or null
 * when nothing of the set's marking reaches a member.
 *
 * Shared by the `details` projection and `projectWatermarkIdentity` so the
 * two cannot answer differently for one record — a body-holding reader and a
 * details-only reader have to agree or the same output is current on one
 * surface and behind on the other.
 */
const watermarksIdentityOf = (data: ProjectRedactionData): string | null =>
  data.watermarksOff || data.watermarks.length === 0
    ? null
    : data.watermarksModifiedDateTime;

/**
 * A projection of the record into the asset's `details`.
 *
 * WHY ANYTHING IS DUPLICATED AT ALL. The body answers everything, but only
 * after a `signedUri` download — two round trips. `details` comes back with
 * the asset-list query, so a caller that only needs to know whether an output
 * is behind can settle it in one. Today every such caller is in the editor
 * and already holds the body; the intended reader is the folder view's
 * download gate, which currently does not check project staleness at all.
 *
 * THE BODY IS AUTHORITATIVE. This is a cache, not a second record: a reader
 * that has the body uses the body, and nothing ever merges the two. They
 * cannot drift at write time because both are produced from one `data` in a
 * single `createAsset` call — the only way to make them disagree is to
 * hand-edit the asset, which is true of every field here.
 *
 * BATES SETTINGS RIDE ALONG, not just the stamps. A complete numbering check
 * needs each member's expected first number, and the folder view can work
 * that out itself from the merge order and the page counts it is already
 * rendering — `startNumber` is the one input it would otherwise lack. The
 * watermarks do NOT ride along: they carry image rasters, and their verdict
 * needs the marks themselves rather than a stamp.
 */
export const buildProjectRedactionAssetDetails = (
  data: ProjectRedactionData
): Record<string, unknown> => ({
  // THE IDENTITY, not the raw stamp — null whenever nothing of the set's
  // marking actually reaches a member, which is what an output records too.
  //
  // The body's `watermarksModifiedDateTime` answers "when did these last
  // change" and keeps answering it after the marks are emptied or switched
  // off. Projecting that verbatim would tell a reader the set is governed by
  // marks that draw nothing, and an output carrying no stamp would read
  // behind when it is exactly right. `projectWatermarkIdentity` collapses
  // both of those to null, which is the comparison the verdict makes.
  ...(watermarksIdentityOf(data)
    ? { watermarksModifiedDateTime: watermarksIdentityOf(data) }
    : {}),
  ...(data.batesModifiedDateTime
    ? { batesModifiedDateTime: data.batesModifiedDateTime }
    : {}),
  ...(data.bates ? { bates: data.bates } : {}),
});

/**
 * Read the projection above, for a caller that has not downloaded the body.
 *
 * Returns what `details` carries and nothing inferred: a caller holding the
 * body should not be here at all, and one that is here must treat a missing
 * field as "not known from this source" rather than as "absent".
 */
export const parseProjectRedactionAssetDetails = (
  details: unknown
): {
  readonly watermarksModifiedDateTime: string | null;
  readonly batesModifiedDateTime: string | null;
  readonly bates: BatesStampSettings | null;
} => {
  let obj: unknown = details;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      obj = null;
    }
  }
  const rec = isRecord(obj) ? obj : {};
  const stamp = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() ? value : null;
  return {
    watermarksModifiedDateTime: stamp(rec.watermarksModifiedDateTime),
    batesModifiedDateTime: stamp(rec.batesModifiedDateTime),
    bates: parseBatesSettings(rec.bates),
  };
};

/** Read a concern's stamp off an asset record's `details`. */
export const readProjectRedactionStamp = (
  details: unknown,
  key: 'watermarksModifiedDateTime' | 'batesModifiedDateTime'
): string | null => {
  let obj: unknown = details;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return null;
    }
  }
  if (!isRecord(obj)) {
    return null;
  }
  const value = obj[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

/**
 * Does a merged document's watermarking govern one named file, and with what?
 *
 * Several answers rather than two, because "nothing governs this file" and
 * "we cannot say yet" are not the same and a caller that reads the second as
 * the first releases a document the set's marking never reached.
 *
 * `unresolved` is never guessed past FOR THE SET: none of its marks draw or
 * burn and no provenance is recorded, so the output reads `behind`. The
 * file's OWN marks still draw — they are legitimately its own. See
 * `watermarksToDraw`.
 */
export type WatermarkGovernance =
  | {
      readonly status: 'governed';
      readonly marks: readonly AppliedWatermark[];
      /**
       * When these marks last changed — the identity an output records.
       *
       * Carried in the answer rather than read separately, so a caller
       * cannot draw one set of marks and record a stamp belonging to
       * another.
       */
      readonly modifiedDateTime: string | null;
    }
  | { readonly status: 'unresolved' }
  /**
   * A set that still has marks, with its switch OFF: nothing draws on this
   * file at all, its own marks included — the switch means "watermarks on
   * this page" and reads the same on every member.
   *
   * Distinct from `withdrawn`, where the set has no marks to govern with.
   */
  | { readonly status: 'suppressed' }
  /**
   * A set whose marks have been REMOVED. Draws the same as `ungoverned` (the
   * file's own marks, nothing from the set) and judges differently: an output
   * burned under the set's marking is still asserting a designation the
   * reviewer withdrew, which is the case that matters — a release that still
   * says DRAFT after DRAFT was taken off.
   */
  | { readonly status: 'withdrawn' }
  | { readonly status: 'ungoverned' };

const UNRESOLVED: WatermarkGovernance = { status: 'unresolved' };
export const UNGOVERNED: WatermarkGovernance = { status: 'ungoverned' };
const WITHDRAWN: WatermarkGovernance = { status: 'withdrawn' };
const SUPPRESSED: WatermarkGovernance = { status: 'suppressed' };

/**
 * WHICH ASSET THE SET'S CURRENT MARKING IS, independent of any member.
 *
 * Three values, like everything else that reads this record: `undefined` is
 * "cannot say yet", `null` is "carries no marking", and a string is the asset
 * an output must name to count as current.
 *
 * It exists because the rule had started to be rewritten wherever it was
 * needed — the governance resolver had it, and the Documents panel had its
 * own copy that agreed only by inspection and got the id wrong, reaching for
 * the whole body's `assetId` where the burn records the watermarks'. The
 * difference between those two is the entire point of splitting
 * `watermarksAssetId` out: a Bates-only write to the same body bumps one and
 * not the other, and the copy that read the wrong one would have ambered
 * every member of the release.
 */
export const projectWatermarkIdentity = (
  record: ProjectRedactionRecord
): string | null | undefined => {
  switch (record.status) {
    case 'unread':
    case 'failed':
      return undefined;
    case 'none':
      return null;
    default:
      // No marks, or marks switched off: nothing of the set's reaches a
      // member, so an output that carries an id is behind and one that
      // carries none is right. Checked before the id for the same reason
      // the resolver checks it before the switch.
      return watermarksIdentityOf(record.data);
  }
};

export const resolveWatermarkGovernance = (args: {
  /** The set's record. Never undefined — absence is `unread`. */
  readonly record: ProjectRedactionRecord;
  /** The set's members, or undefined when the membership has NOT been read. */
  readonly members: readonly TDOId[] | undefined;
  readonly tdoId: TDOId | string | undefined;
}): WatermarkGovernance => {
  const { record, members, tdoId } = args;
  if (!tdoId) {
    return UNGOVERNED;
  }
  // DEFINITE ANSWERS FIRST. Asking "has membership loaded?" before "does the
  // set carry marks?" reports `unresolved` for every request with a merged
  // document and no set-wide marks, for the whole window to
  // LOAD_PROJECT_SUCCESS — and `unresolved` draws and burns nothing.
  if (
    members !== undefined &&
    !members.some((id) => String(id) === String(tdoId))
  ) {
    // Not a member. Nothing the set does can govern this file, whatever its
    // record says or has not said yet.
    return UNGOVERNED;
  }
  if (record.status === 'none') {
    // The set carries no record. Membership is irrelevant: there is nothing
    // to inherit either way.
    return UNGOVERNED;
  }
  if (record.status === 'unread' || record.status === 'failed') {
    return UNRESOLVED;
  }
  if (members === undefined) {
    // The set HAS marks and we cannot yet say whether this file is in it.
    // The only genuinely unknown case left.
    return UNRESOLVED;
  }
  // NO MARKS TO GOVERN WITH: the file's own rule applies again. Before the
  // switch, because a switch over an empty list says nothing.
  if (record.data.watermarks.length === 0) {
    return WITHDRAWN;
  }
  // SWITCHED OFF, with marks: nothing draws on this file, its own included.
  if (record.data.watermarksOff) {
    return SUPPRESSED;
  }
  return {
    status: 'governed',
    marks: record.data.watermarks,
    // THE SAME EXPRESSION the panel and the folder collector read, because
    // an output is compared against whichever of them answered.
    modifiedDateTime: projectWatermarkIdentity(record) ?? null,
  };
};

/**
 * Does this file's output still carry the set's current watermarks?
 *
 * Three values, not the Bates resolver's four: no `orphaned`, because a
 * former member's leftover NUMBERS collide with whatever took its place in
 * the order while a leftover watermark collides with nothing. And no position
 * arithmetic — a watermark is identical on every member, so a reorder
 * invalidates nothing.
 */
export type WatermarkVerdict = 'current' | 'behind' | 'unknown';

export const watermarkStalenessVerdict = (args: {
  readonly hasOutput: boolean;
  readonly governance: WatermarkGovernance;
  /**
   * The watermark STAMP the output was burned under, or null when it carries
   * no set marking — because it was burned before the set had marks, or from
   * its own. See the header for why a stamp and not a counter.
   */
  readonly carriedModifiedDateTime: string | null;
}): WatermarkVerdict => {
  // Nothing produced yet: unmarked, not behind.
  if (!args.hasOutput) {
    return 'current';
  }
  switch (args.governance.status) {
    case 'unresolved':
      // A check that cannot run must not answer "fine" — and must not raise a
      // warning either.
      return 'unknown';
    case 'withdrawn':
    case 'suppressed':
      // The set's marking no longer reaches this file. An output burned under
      // it is still asserting what was taken away.
      return args.carriedModifiedDateTime === null ? 'current' : 'behind';
    case 'ungoverned':
      // Whatever the output carries is its own business, including a record
      // id from a set it has since left.
      return 'current';
    default:
      // An unconfirmed optimistic write has no asset yet, so everything reads
      // `behind` for that moment — the safe direction, and it settles on the
      // next load.
      return args.governance.modifiedDateTime !== null &&
        args.carriedModifiedDateTime === args.governance.modifiedDateTime
        ? 'current'
        : 'behind';
  }
};

/**
 * The same verdict for a caller holding only the set's IDENTITY — no body,
 * and so no marks.
 *
 * FOR THE SURFACES THAT JUDGE WITHOUT OPENING THE ASSET: the folder view and
 * the Download Review modal read the stamp out of the asset's `details`,
 * which arrives with the asset list, and never download a body. Everything
 * the verdict needs is in that stamp — the marks themselves only matter for
 * DRAWING.
 *
 * Returns a verdict rather than a `WatermarkGovernance`, deliberately. The
 * governed branch of that type carries `marks`, and the only honest value
 * here is an empty list; handing one back would let a caller draw nothing
 * and believe it had drawn the set's marking.
 *
 * `identity` is `projectWatermarkIdentity`'s three values: `undefined` has
 * not been read, `null` is nothing of the set's reaching a member — the
 * withdrawn and suppressed cases, which judge alike — and a string is the
 * stamp an output must carry.
 */
export const watermarkStalenessFromIdentity = (args: {
  readonly hasOutput: boolean;
  /** `undefined` while the membership has not been read. */
  readonly isMember: boolean | undefined;
  readonly identity: string | null | undefined;
  readonly carriedModifiedDateTime: string | null;
}): WatermarkVerdict =>
  watermarkStalenessVerdict({
    hasOutput: args.hasOutput,
    // DEFINITE ANSWERS FIRST, in `resolveWatermarkGovernance`'s order: a
    // non-member is ungoverned whatever the record says or has not said yet.
    governance:
      args.isMember === false
        ? UNGOVERNED
        : args.identity === undefined || args.isMember === undefined
          ? UNRESOLVED
          : args.identity === null
            ? WITHDRAWN
            : {
                status: 'governed',
                marks: [],
                modifiedDateTime: args.identity,
              },
    carriedModifiedDateTime: args.carriedModifiedDateTime,
  });

/**
 * The file's own marks, minus any that are literally the SET's mark — a
 * shared `instanceId` means one object in two records, which a mark promoted
 * to the set leaves behind in a session that has not caught up.
 *
 * NOT the supersession rule below, which is about two DIFFERENT marks of the
 * same library watermark and keeps the loser so it can return. Here there is
 * nothing to keep.
 */
export const dropDuplicateInstances = (
  setMarks: readonly AppliedWatermark[],
  ownMarks: readonly AppliedWatermark[]
): readonly AppliedWatermark[] => {
  if (setMarks.length === 0 || ownMarks.length === 0) {
    return ownMarks;
  }
  const setInstanceIds = new Set(setMarks.map((w) => w.instanceId));
  return ownMarks.filter((w) => !setInstanceIds.has(w.instanceId));
};

/**
 * What a member of a merged document actually draws: the set's marks plus its
 * own (VE-30104).
 *
 * A UNION, not a precedence rule — two watermarks are two layers, where two
 * Bates sequences on a page would be nonsense. The set's go FIRST so they
 * draw underneath; the document's own mark is the more specific instruction.
 *
 * ONE EXCEPTION: a file already carrying a library watermark since applied to
 * the whole set would draw it twice, overlapped at double opacity, on a
 * released page. The set's copy wins and the file's is SUPPRESSED, not
 * deleted, so switching the set's off brings it back as it was.
 *
 * Matched on `watermarkId`, the only thing that says "the same watermark"
 * across two documents. An ad-hoc mark has none and is never suppressed —
 * two hand-made CONFIDENTIALs are two marks.
 */
export const mergeSetAndOwnWatermarks = (
  setMarks: readonly AppliedWatermark[],
  ownMarks: readonly AppliedWatermark[]
): readonly AppliedWatermark[] => {
  if (setMarks.length === 0) {
    return ownMarks;
  }
  const setLibraryIds = new Set(
    setMarks
      .map((w) => w.watermarkId)
      .filter((id): id is string => typeof id === 'string' && id !== '')
  );
  // The same mark in both lists is dropped first: it is one object, and the
  // library-id rule below is about two.
  const kept = dropDuplicateInstances(setMarks, ownMarks).filter(
    (w) => !w.watermarkId || !setLibraryIds.has(w.watermarkId)
  );
  return kept.length === 0 ? setMarks : [...setMarks, ...kept];
};

/**
 * WHAT GETS DRAWN ON A FILE — the one rule, for the page and for the burn.
 *
 * ONE HOME, TWO CALLERS: a second copy is free to disagree, and what
 * disagrees is what a released file carries versus what the page told the
 * reviewer it would carry.
 *
 * - `governed`: the set's marks AND the file's own, with the file's own
 *   switch NOT consulted — one switch speaks for the page.
 * - `suppressed`: nothing at all, the file's own marks included.
 * - everything else: the file's own marks under its own switch.
 */
export const watermarksToDraw = (args: {
  readonly governance: WatermarkGovernance;
  readonly ownMarks: readonly AppliedWatermark[];
  readonly ownWatermarksOff: boolean;
}): readonly AppliedWatermark[] => {
  const { governance, ownMarks, ownWatermarksOff } = args;
  switch (governance.status) {
    case 'governed':
      return mergeSetAndOwnWatermarks(governance.marks, ownMarks);
    case 'suppressed':
      return [];
    default:
      return ownWatermarksOff ? [] : ownMarks;
  }
};

/**
 * WHERE A MARK'S RASTER MAY BE READ FROM — an allowlist, not the body's word.
 *
 * `serviceLoadWatermarkImageAsset` fails closed when an asset's container
 * does not match what it is given — but both the asset id and the container
 * come out of the same org-writable body, so that check compares the body
 * against itself. Anyone who can write it could name any asset in any TDO,
 * which a reviewer's token would fetch and the burn would draw onto a
 * released record.
 *
 * So a recorded container is honoured only when this reader already holds it:
 * the project the record came from, or one of its members. Null means
 * REFUSED, which callers must treat as a failure and not as grounds to fall
 * back.
 */
export const allowedRasterContainer = (args: {
  readonly recorded: string | null | undefined;
  /** Where this reader would look if the mark named nowhere. */
  readonly fallback: TDOId;
  /** The containers this reader legitimately holds. */
  readonly allowed: readonly (TDOId | string)[];
}): TDOId | null => {
  const { recorded, fallback, allowed } = args;
  if (!recorded) {
    return fallback;
  }
  if (String(recorded) === String(fallback)) {
    return fallback;
  }
  return allowed.some((id) => String(id) === String(recorded))
    ? (recorded as TDOId)
    : null;
};
